import React, { useEffect, useState, useRef } from "react";
import { supabase } from "./supabase";
import {
  Crown, DollarSign, PauseCircle, XCircle, CheckCircle, Search, Filter, Eye, ChevronDown
} from "lucide-react";

// CustomDropdown UNIFICADO (copiado de FacturasComprobantes.js)
const CustomDropdown = ({ value, onChange, options, placeholder, disabled = false, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find(opt => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '14px 16px',
          border: '2px solid #f1f5f9',
          borderRadius: '12px',
          background: disabled ? '#f8fafc' : 'white',
          color: disabled ? '#94a3b8' : '#1e293b',
          fontSize: '16px',
          fontWeight: '500',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '44px',
          touchAction: 'manipulation',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#f1f5f9';
          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          {Icon && <Icon size={16} color="#64748b" />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown 
          size={16} 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            flexShrink: 0,
            color: '#64748b'
          }} 
        />
      </button>
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          marginTop: '4px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: option.value === value ? '#eff6ff' : 'transparent',
                color: option.value === value ? '#3b82f6' : '#374151',
                fontSize: '16px',
                fontWeight: option.value === value ? '600' : '400',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                touchAction: 'manipulation'
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = '#f8fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ESTADOS_SUSCRIPCION = {
  authorized: { color: "#22c55e", label: "Activa", icon: <CheckCircle size={16} /> },
  paused: { color: "#f59e0b", label: "Pausada", icon: <PauseCircle size={16} /> },
  cancelled: { color: "#ef4444", label: "Cancelada", icon: <XCircle size={16} /> },
  pending: { color: "#3b82f6", label: "Pendiente", icon: <PauseCircle size={16} /> },
  default: { color: "#64748b", label: "Desconocido", icon: <PauseCircle size={16} /> }
};

function AdminSuscripcionesPagos() {
  const [suscripciones, setSuscripciones] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroEmail, setFiltroEmail] = useState("");
  const [modalDetalle, setModalDetalle] = useState({ open: false, data: null, tipo: null });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    cargarDatos();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    let { data: subs } = await supabase.from('suscripciones').select('*').order('created_at', { ascending: false });
    let { data: pays } = await supabase.from('pagos').select('*').order('created_at', { ascending: false });
    setSuscripciones(subs || []);
    setPagos(pays || []);
    setLoading(false);
  };

  const suscripcionesFiltradas = suscripciones.filter(s =>
    (!filtroEstado || s.estado === filtroEstado) &&
    (!filtroEmail || (s.email && s.email.toLowerCase().includes(filtroEmail.toLowerCase())))
  );

  // Header y color de vista
  const colorVista = {
    gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    solid: '#64748b',
    light: '#64748b10',
    description: 'Suscripciones y Pagos'
  };

  // Columnas de la tabla de suscripciones
  const columnasSuscripciones = [
    { title: "ID", dataIndex: "mp_subscription_id", key: "id", width: 120, ellipsis: true },
    { title: "Email", dataIndex: "email", key: "email", width: 180 },
    { title: "Estado", dataIndex: "estado", key: "estado", width: 120, render: (estado) => {
      const info = ESTADOS_SUSCRIPCION[estado] || ESTADOS_SUSCRIPCION.default;
      return <span style={{ color: info.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>{info.icon}{info.label}</span>;
    } },
    { title: "Monto", dataIndex: "monto", key: "monto", width: 100, render: v => v ? `$${v}` : "-" },
    { title: "Moneda", dataIndex: "moneda", key: "moneda", width: 80 },
    { title: "Plan", dataIndex: "plan", key: "plan", width: 120 },
    { title: "Trial", dataIndex: "trial_days", key: "trial_days", width: 80 },
    { title: "Descuento", dataIndex: "discount_code", key: "discount_code", width: 120 },
    { title: "Creada", dataIndex: "created_at", key: "created_at", width: 160, render: v => v && new Date(v).toLocaleString() },
    { title: "Próx. Pago", dataIndex: "fecha_proximo_pago", key: "fecha_proximo_pago", width: 160, render: v => v && new Date(v).toLocaleString() },
    { title: "Acciones", key: "acciones", width: 180, render: (_, row) => (
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          style={{
            background: colorVista.gradient,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '6px 14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
          onClick={() => setModalDetalle({ open: true, data: row, tipo: 'suscripcion' })}
        >
          <Eye size={16} /> Ver
        </button>
        {(row.estado === 'authorized' || row.estado === 'paused') && (
          <button
            style={{
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
            onClick={async () => {
              if (window.confirm('¿Seguro que deseas cancelar esta suscripción?')) {
                await cancelarSuscripcion(row.mp_subscription_id);
              }
            }}
          >
            <XCircle size={16} /> Cancelar
          </button>
        )}
      </div>
    ) }
  ];

  // Columnas de la tabla de pagos
  const columnasPagos = [
    { title: "ID Pago", dataIndex: "mp_payment_id", key: "id", width: 120, ellipsis: true },
    { title: "Suscripción", dataIndex: "mp_subscription_id", key: "mp_subscription_id", width: 120, ellipsis: true },
    { title: "Email MP", dataIndex: "email", key: "email", width: 180, render: v => v || "-" },
    { title: "Email Usuario", dataIndex: "email_usuario", key: "email_usuario", width: 180, render: v => v || "-" },
    { title: "Estado", dataIndex: "estado", key: "estado", width: 120 },
    { title: "Bruto", dataIndex: "monto_bruto", key: "monto_bruto", width: 100, render: v => v ? `$${v}` : "-" },
    { title: "Neto", dataIndex: "monto_neto", key: "monto_neto", width: 100, render: v => v ? `$${v}` : "-" },
    { title: "Comisión", dataIndex: "comision", key: "comision", width: 100, render: v => v ? `$${v}` : "-" },
    { title: "Moneda", dataIndex: "moneda", key: "moneda", width: 80 },
    { title: "Fecha Pago", dataIndex: "fecha_pago", key: "fecha_pago", width: 160, render: v => v && new Date(v).toLocaleString() },
    { title: "Acciones", key: "acciones", width: 180, render: (_, row) => (
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          style={{
            background: colorVista.gradient,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '6px 14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
          onClick={() => setModalDetalle({ open: true, data: row, tipo: 'pago' })}
        >
          <Eye size={16} /> Ver
        </button>
        {row.estado === 'approved' && (
          <button
            style={{
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
            onClick={async () => {
                      if (window.confirm('¿Seguro que deseas reembolsar este pago?')) {
          await reembolsarPago(row);
        }
            }}
          >
            <DollarSign size={16} /> Reembolsar
          </button>
        )}
      </div>
    ) }
  ];

  // Dashboard de métricas
  const totalActivas = suscripciones.filter(s => s.estado === 'authorized').length;
  const totalCanceladas = suscripciones.filter(s => s.estado === 'cancelled').length;
  const totalPausadas = suscripciones.filter(s => s.estado === 'paused').length;
  const totalIngresos = pagos.reduce((acc, p) => acc + (p.monto_neto || 0), 0);
  const totalComisiones = pagos.reduce((acc, p) => acc + (p.comision || 0), 0);

  // Función para cancelar suscripción
  async function cancelarSuscripcion(row) {
    if (!window.confirm('¿Seguro que deseas cancelar esta suscripción?')) return;
    try {
      const res = await fetch(`/api/subscription/${row.mp_id}/cancel`, { method: 'PUT' });
      const data = await res.json();
      if (data.success) {
        alert('Suscripción cancelada correctamente.');
        // Actualizar tabla (puedes llamar a la función de recarga de datos si existe)
        if (typeof cargarDatos === 'function') cargarDatos();
      } else {
        alert('Error al cancelar: ' + (data.error || 'Error desconocido.'));
      }
    } catch (err) {
      alert('Error al cancelar: ' + err.message);
    }
  }

  // Función para reembolsar pago
  async function reembolsarPago(row) {
    if (!window.confirm('¿Seguro que deseas reembolsar este pago?')) return;
    
    const paymentId = row.mp_payment_id;
    
    if (!paymentId) {
      alert('Error: No se encontró el ID del pago');
      return;
    }
    
    try {
      const res = await fetch('https://nokk-backend.vercel.app/api/refund-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mp_payment_id: paymentId })
      });
      const data = await res.json();
      if (data.success) {
        alert('Pago reembolsado correctamente.');
        if (typeof cargarDatos === 'function') cargarDatos();
      } else {
        alert('Error al reembolsar: ' + (data.error || 'Error desconocido.'));
      }
    } catch (err) {
      alert('Error al reembolsar: ' + err.message);
    }
  }

  return (
    <div className="main-container" style={{
      padding: isMobile ? '16px 12px 120px 12px' : '20px',
      maxWidth: '100%',
      margin: '0 auto',
      background: '#f8fafc',
      minHeight: '100vh',
      transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      animation: 'fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Header unificado */}
      <div className="header-section" style={{
        marginBottom: '20px',
        animation: 'slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minHeight: '56px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: colorVista.gradient,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 16px ${colorVista.light}`,
            flexShrink: 0
          }}>
            <Crown size={20} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: '700',
              color: '#0f172a',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              Panel Suscripciones & Pagos
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              margin: 0,
              fontWeight: '400',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              Administración profesional de MercadoPago y Supabase
            </p>
          </div>
        </div>
        {/* Filtros */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
          gap: '16px',
          marginTop: '20px',
          marginBottom: '10px'
        }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b',
              zIndex: 1
            }} />
            <input
              type="text"
              placeholder="Buscar por email..."
              value={filtroEmail}
              onChange={e => setFiltroEmail(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: '100%',
                padding: '14px 16px 14px 44px',
                border: '2px solid #f1f5f9',
                borderRadius: '12px',
                fontSize: '16px',
                WebkitAppearance: 'none',
                appearance: 'none',
                background: 'white',
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                outline: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxSizing: 'border-box',
                touchAction: 'manipulation'
              }}
              onFocus={e => {
                e.target.style.borderColor = colorVista.solid;
                e.target.style.boxShadow = `0 0 0 3px ${colorVista.light}`;
              }}
              onBlur={e => {
                e.target.style.borderColor = '#f1f5f9';
                e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
              }}
            />
          </div>
          <CustomDropdown
            value={filtroEstado}
            onChange={setFiltroEstado}
            options={Object.entries(ESTADOS_SUSCRIPCION).map(([k, v]) => ({ value: k, label: v.label }))}
            placeholder="Filtrar por estado"
            icon={Filter}
          />
          <button
            onClick={cargarDatos}
            style={{
              background: colorVista.gradient,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 20px',
              fontWeight: 600,
              fontSize: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <Filter size={18} /> Actualizar
          </button>
        </div>
      </div>
      {/* Stats compactas (solo desktop) */}
      {!isMobile && (
        <div className="stats-section" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '12px',
          marginBottom: '20px',
          animation: 'fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '18px', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircle size={22} color="#22c55e" />
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{totalActivas}</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>Activas</div>
            </div>
          </div>
          <div style={{ background: 'white', borderRadius: '16px', padding: '18px', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
            <XCircle size={22} color="#ef4444" />
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{totalCanceladas}</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>Canceladas</div>
            </div>
          </div>
          <div style={{ background: 'white', borderRadius: '16px', padding: '18px', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
            <PauseCircle size={22} color="#f59e0b" />
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{totalPausadas}</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>Pausadas</div>
            </div>
          </div>
          <div style={{ background: 'white', borderRadius: '16px', padding: '18px', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
            <DollarSign size={22} color="#3b82f6" />
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{`$${totalIngresos.toLocaleString()}`}</div>
              <div style={{ color: '#64748b', fontSize: 13 }}>Ingresos Netos</div>
            </div>
          </div>
        </div>
      )}
      {/* Tablas */}
      <div style={{ marginTop: 10 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: colorVista.solid, marginBottom: 8 }}>Suscripciones</h2>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          border: '1px solid #f1f5f9',
          marginBottom: 32
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {columnasSuscripciones.map(col => (
                    <th key={col.key} style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columnasSuscripciones.length} style={{ textAlign: 'center', padding: 40 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            border: "2px solid #e2e8f0",
                            borderTop: "2px solid #3b82f6",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        <span style={{ color: '#64748b', fontSize: '14px' }}>Cargando...</span>
                      </div>
                    </td>
                  </tr>
                ) : suscripcionesFiltradas.length === 0 ? (
                  <tr><td colSpan={columnasSuscripciones.length} style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No hay suscripciones</td></tr>
                ) : (
                  suscripcionesFiltradas.map((row, idx) => (
                    <tr key={row.mp_subscription_id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      {columnasSuscripciones.map(col => (
                        <td key={col.key} style={{ padding: '14px 18px', fontSize: 15, color: '#334155', fontWeight: col.key === 'acciones' ? 600 : 400 }}>{col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: colorVista.solid, marginBottom: 8 }}>Pagos</h2>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          border: '1px solid #f1f5f9',
          marginBottom: 32
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {columnasPagos.map(col => (
                    <th key={col.key} style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columnasPagos.length} style={{ textAlign: 'center', padding: 40 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            border: "2px solid #e2e8f0",
                            borderTop: "2px solid #3b82f6",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        <span style={{ color: '#64748b', fontSize: '14px' }}>Cargando...</span>
                      </div>
                    </td>
                  </tr>
                ) : pagos.length === 0 ? (
                  <tr><td colSpan={columnasPagos.length} style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No hay pagos</td></tr>
                ) : (
                  pagos.map((row, idx) => (
                    <tr key={row.mp_payment_id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      {columnasPagos.map(col => (
                        <td key={col.key} style={{ padding: '14px 18px', fontSize: 15, color: '#334155', fontWeight: col.key === 'acciones' ? 600 : 400 }}>{col.render ? col.render(row[col.dataIndex], row) : row[col.dataIndex]}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Modal de detalle */}
      {modalDetalle.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 3000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: 32,
            width: isMobile ? '95vw' : 700,
            maxWidth: '100vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colorVista.solid, marginBottom: 16 }}>
              {modalDetalle.tipo === 'suscripcion' ? 'Detalle Suscripción' : 'Detalle Pago'}
            </h3>
            <pre style={{ maxHeight: 400, overflow: 'auto', background: '#f6f8fa', padding: 16, borderRadius: 8, fontSize: 14 }}>
              {JSON.stringify(modalDetalle.data, null, 2)}
            </pre>
            <button
              onClick={() => setModalDetalle({ open: false, data: null, tipo: null })}
              style={{
                marginTop: 18,
                background: colorVista.gradient,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 24px',
                fontWeight: 600,
                fontSize: '16px',
                cursor: 'pointer',
                float: 'right',
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSuscripcionesPagos;
